import { useMemo } from 'react';
import { Badge, Heading, Inline, vars } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import { constants } from '@eti/utils';
import { cancelForAnyReason, flexibleTicket } from '@eti/products';
import type { AccordionInterface } from '../../../common';
import { AccordionComponent } from '../../../common';
import type { TicketType, TicketsProps } from '../../model';
import { TicketInformation } from '../cards/common/TicketInformation';
import { TicketReadMore } from '../common/TicketReadMore';
import { PriceLabel, TicketPrice } from '../common/PriceInfoLabel';
import { POPULAR_PILL_SHOW_TYPE, TICKET_TYPES } from '../../constants';

interface TicketV2Props extends TicketsProps {
  hasError: boolean;
  onSelect: (ticketType: TicketType) => void;
}

const { light } = vars.colors.signal.critical;
const { AIR_REBOOKING } = constants.SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS;

const pricePerPersonStyle = css`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  flex-grow: 2;
`;

const headingStyles = css`
  font-size: 0.875rem;
`;

const inlineErrorClass = css`
  color: ${light};
  padding-top: 10px;
`;

const ticketV2Class = css`
  .accordion-option-header {
    align-items: center;
    display: flex;
    gap: 8px;
  }

  .accordion-component-option {
    position: relative;
  }
`;

const titleStyles = (shouldShowPopularPillOnTop: boolean) => css`
  @media ${mediaQueries.small.only} {
    ${shouldShowPopularPillOnTop && 'margin-bottom:9px;'}
  }
`;

const popularPillStyles = (shouldShowPopularPillInside: boolean) => css`
  position: absolute;
  right: 41%;
  ${shouldShowPopularPillInside ? 'top: 22px;' : 'top: -16px;'}

  @media ${mediaQueries.small.only} {
    ${shouldShowPopularPillInside && 'display:none;'}
    ${!shouldShowPopularPillInside && 'right:37%;'}
  }
`;

export const TicketV2 = ({ hasError, onSelect, selectedTicketType, tickets }: TicketV2Props) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const flexBehaviour = p('IbeClient.SearchResult.Flex.Behaviour');
  const ticketOptionsTitle = t('Result.Page.SelectTicketType.Text');
  const ticketId = flexBehaviour === AIR_REBOOKING ? flexibleTicket.id : cancelForAnyReason.id;
  const popularPillShowType = p('Result.Page.Drawer.PopularPill.ShowType')?.toLowerCase();
  const popularPillLabel = t('Result.Page.Drawer.PopularPill.Label');
  const isFlexibleTicketOnTop = p('Result.Page.Ticket.Reversed');
  const shouldShowPopularPillOnTop =
    isFlexibleTicketOnTop && popularPillShowType === POPULAR_PILL_SHOW_TYPE.TOP;
  const shouldShowPopularPillInside =
    !isFlexibleTicketOnTop && popularPillShowType === POPULAR_PILL_SHOW_TYPE.INSIDE;

  const accordionOptions = useMemo<Array<AccordionInterface>>(() => {
    const ticketOptions: Array<AccordionInterface> = tickets.map(
      ({ ticketType, ticketTitle, pricePerPerson, priceDisplayText }) => ({
        id: ticketType,
        header: (
          <>
            {(shouldShowPopularPillOnTop || shouldShowPopularPillInside) &&
              ticketType === TICKET_TYPES.FLEXIBLE_TICKET && (
                <Badge
                  className={popularPillStyles(shouldShowPopularPillInside)}
                  data-testid="popular-pill"
                  variant="promotional"
                >
                  {popularPillLabel}
                </Badge>
              )}
            <div data-testid={`ticket-${ticketType}`}>
              <Heading className={headingStyles} level={4}>
                {ticketTitle}
              </Heading>
            </div>
            <div className={pricePerPersonStyle}>
              <TicketPrice ticketVersion={2}> {pricePerPerson} </TicketPrice>
              <PriceLabel isAtBottom={false}>{priceDisplayText}</PriceLabel>
            </div>
          </>
        ),
        body: <TicketInformation ticketType={ticketType} ticketVersion={2} />,
      }),
    );

    return ticketOptions;
  }, [shouldShowPopularPillOnTop, shouldShowPopularPillInside, popularPillLabel, tickets]);

  return (
    <div data-testid="ticket-version-2">
      <AccordionComponent
        className={ticketV2Class}
        defaultValue={selectedTicketType}
        footer={<TicketReadMore ticketId={ticketId} />}
        hasError={hasError}
        onChange={(ticketType) => onSelect(ticketType as TicketType)}
        options={accordionOptions}
        showRadioInput
        title={
          <>
            <Heading className={titleStyles(shouldShowPopularPillOnTop)} level={3}>
              {ticketOptionsTitle}
            </Heading>
            {hasError && (
              <Inline
                className={inlineErrorClass}
                id="select-ticket-type-validation-error"
                tabIndex={-1}
              >
                {t('Result.Page.SelectTicketType.Validation.Text')}
              </Inline>
            )}
          </>
        }
      />
    </div>
  );
};
